export const LEGAL_PAGES_KEY = 'zobu_legal_pages';

export interface LegalSection {
  id: string;
  heading: string;
  body: string;
}

export interface LegalPageData {
  id: 'terms-of-use' | 'privacy' | 'code-of-ethics';
  title: string;
  lastUpdated: string;
  quote?: string;
  intro: string;
  sections: LegalSection[];
}

export const mockLegalPages: LegalPageData[] = [
  {
    id: 'code-of-ethics',
    title: 'Code of Ethics',
    lastUpdated: '2025-01-01',
    quote: 'Impartially on Your Side',
    intro: 'As a Certified Management Consultant (CMC), Hakan Zobu and the entire Zobu Consulting team are committed to upholding the ethical standards of the global management consulting profession. The principles below govern every client engagement and business relationship.',
    sections: [
      {
        id: 'coe-1',
        heading: 'Client Interest First',
        body: 'We always place our client\'s best interests above our own. Our advice is impartial, objective and free from any hidden agenda. The client\'s success is our only measure of success.',
      },
      {
        id: 'coe-2',
        heading: 'Integrity & Honesty',
        body: 'We maintain the highest standards of honesty in all dealings. We do not misrepresent our qualifications, experience or the scope of our services.',
      },
      {
        id: 'coe-3',
        heading: 'Confidentiality',
        body: 'All client information is treated with the strictest confidentiality. We do not disclose proprietary or sensitive information without express written consent.',
      },
      {
        id: 'coe-4',
        heading: 'Competence',
        body: 'We only undertake assignments for which we have demonstrated competence. We continuously develop our professional knowledge and skills.',
      },
      {
        id: 'coe-5',
        heading: 'Independence',
        body: 'We declare any potential conflicts of interest. We maintain professional independence and do not allow any relationship to compromise the objectivity of our advice.',
      },
      {
        id: 'coe-6',
        heading: 'Fair Fees',
        body: 'Our fees are agreed in advance and reflect the value delivered. We do not charge for work not performed and we are transparent about all billing matters.',
      },
      {
        id: 'coe-7',
        heading: 'Respect for Colleagues',
        body: 'We treat fellow professionals with respect, avoid disparaging competitors, and contribute positively to the consulting profession.',
      },
      {
        id: 'coe-8',
        heading: 'Legal Compliance',
        body: 'We operate in full compliance with all applicable laws and regulations in every jurisdiction in which we practice.',
      },
    ],
  },
  {
    id: 'terms-of-use',
    title: 'Terms of Use',
    lastUpdated: '2025-01-01',
    intro: 'Please read these Terms of Use carefully before accessing or using the Zobu Consulting website. By accessing this website, you agree to be bound by these terms.',
    sections: [
      {
        id: 'tu-1',
        heading: 'Acceptance of Terms',
        body: 'By accessing and using this website, you accept and agree to be bound by these Terms of Use and our Privacy Policy. If you do not agree to these terms, please do not use this website.',
      },
      {
        id: 'tu-2',
        heading: 'Use of Website',
        body: 'This website is provided for informational purposes only. You may not use this website for any unlawful purpose or in any way that could damage, disable, or impair the website. Zobu Consulting reserves the right to modify or discontinue any part of this website at any time without notice.',
      },
      {
        id: 'tu-3',
        heading: 'Intellectual Property',
        body: 'All content on this website, including text, graphics, logos, and images, is the property of Zobu Consulting and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without prior written permission.',
      },
      {
        id: 'tu-4',
        heading: 'Disclaimer of Warranties',
        body: 'This website is provided on an "as is" basis without any warranties, express or implied. Zobu Consulting does not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components.',
      },
      {
        id: 'tu-5',
        heading: 'Limitation of Liability',
        body: 'Zobu Consulting shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of this website, even if advised of the possibility of such damages.',
      },
      {
        id: 'tu-6',
        heading: 'Governing Law',
        body: 'These Terms of Use shall be governed by and construed in accordance with the laws of the Republic of Turkey. Any disputes arising under these terms shall be subject to the exclusive jurisdiction of the courts of Istanbul, Turkey.',
      },
      {
        id: 'tu-7',
        heading: 'Changes to Terms',
        body: 'Zobu Consulting reserves the right to modify these Terms of Use at any time. Your continued use of this website following any changes constitutes your acceptance of the revised terms.',
      },
      {
        id: 'tu-8',
        heading: 'Contact',
        body: 'If you have any questions about these Terms of Use, please contact us at info@zobu-consulting.com.',
      },
    ],
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    lastUpdated: '2025-01-01',
    intro: 'Zobu Consulting is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal information when you visit our website or engage with our services.',
    sections: [
      {
        id: 'pp-1',
        heading: 'Information We Collect',
        body: 'We may collect personal information that you voluntarily provide, such as your name, email address, and phone number when you contact us or submit an inquiry. We also collect non-personal information automatically, including browser type, pages visited, and time spent on the site.',
      },
      {
        id: 'pp-2',
        heading: 'How We Use Your Information',
        body: 'We use the information we collect to respond to your inquiries, provide consulting services, improve our website, and send you relevant updates with your consent. We do not sell, trade, or rent your personal information to third parties.',
      },
      {
        id: 'pp-3',
        heading: 'Cookies',
        body: 'Our website may use cookies to enhance your browsing experience. Cookies are small text files placed on your device. You may configure your browser to refuse cookies, but some features of this website may not function properly as a result.',
      },
      {
        id: 'pp-4',
        heading: 'Data Security',
        body: 'We implement appropriate technical and organisational measures to protect your personal information against unauthorised access, alteration, disclosure, or destruction. However, no method of transmission over the internet is completely secure.',
      },
      {
        id: 'pp-5',
        heading: 'Third-Party Links',
        body: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party websites you visit.',
      },
      {
        id: 'pp-6',
        heading: 'Your Rights',
        body: 'Under applicable data protection laws, you have the right to access, correct, or delete your personal information held by us. To exercise these rights, please contact us at info@zobu-consulting.com. We will respond to your request within 30 days.',
      },
      {
        id: 'pp-7',
        heading: 'Retention of Data',
        body: 'We retain your personal information only for as long as necessary to fulfil the purposes outlined in this Privacy Policy, or as required by applicable law.',
      },
      {
        id: 'pp-8',
        heading: 'Changes to This Policy',
        body: 'We may update this Privacy Policy from time to time. The updated version will be indicated by a revised date at the top of this page. We encourage you to review this policy periodically.',
      },
      {
        id: 'pp-9',
        heading: 'Contact Us',
        body: 'If you have questions or concerns about this Privacy Policy or our data practices, please contact us at info@zobu-consulting.com or write to us at our office address listed on the Contact page.',
      },
    ],
  },
];
